import {
  hashKey,
  useQuery,
  type Query,
  type QueryClient,
  type QueryKey,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { useSyncExternalStore } from "react";

export type StreamStatus = "idle" | "connecting" | "connected" | "reconnecting" | "error";

/** What the definition hands its stream: where to keep the resume cursor, and its callbacks. */
export interface StreamHandlers<Event> {
  sessionStorageKey: string;
  onOpen: () => void;
  onClose: () => void;
  onReconnect: () => void;
  onReconnectExhausted: () => void;
  onEvent: (event: Event) => void;
}

/** One frame of events, after the definition folded them into the query's data. */
export interface StreamedBatch<Variables, Data, Event> {
  variables: Variables;
  events: readonly Event[];
  previous: Data | undefined;
  data: Data | undefined;
}

export interface StreamedQueryDefinitionOptions<Variables, Data, Event> {
  /** The query that loads the data. Its key is the cache entry the stream keeps current. */
  query: (variables: Variables) => { queryKey: QueryKey };
  /** Opens the stream for that data. */
  stream: (variables: Variables, handlers: StreamHandlers<Event>) => { disconnect: () => void };
  /** Folds a frame of events into the data. Must not mutate what it is given. */
  reduce: (data: Data | undefined, events: readonly Event[]) => Data | undefined;
}

// A remount (a route change that keeps the reader, React's strict-mode double mount) reads the
// same data again right away, so the stream waits this long before it closes.
const CLOSE_DELAY_MS = 1000;

// A tab whose animation frames are throttled still drains at the server's own batch size.
const MAX_BATCH_SIZE = 200;

interface StreamedMeta {
  streamedQuery?: { owner: object; variables: unknown; stream: boolean };
}

interface OpenStream {
  close: () => void;
  closeTimer: ReturnType<typeof setTimeout> | null;
  /** The stream ran out of reconnect attempts; whoever reads the data next opens a new one. */
  gaveUp: boolean;
}

/**
 * Server data that a query loads and a stream keeps current.
 *
 * The data lives in the query cache like any other query: loaders prefetch it, components read
 * it with `useQuery`, and writes go through `setQueryData`. The stream follows its readers. It
 * opens once a mounted reader's query has data, so it always resumes on top of what was loaded,
 * and closes shortly after the last reader goes. Its events are folded in once per frame.
 */
export function createStreamedQueryDefinition<Variables, Data, Event>(
  queryClient: QueryClient,
  options: StreamedQueryDefinitionOptions<Variables, Data, Event>
) {
  const owner = {};
  const streams = new Map<string, OpenStream>();
  const statuses = new Map<string, StreamStatus>();
  const statusListeners = new Set<() => void>();
  const batchListeners = new Set<(batch: StreamedBatch<Variables, Data, Event>) => void>();

  const queryOptions = (variables: Variables, stream = true) =>
    ({
      ...options.query(variables),
      // The stream keeps the data current; a refetch would rewind what it applied.
      staleTime: Infinity,
      // Each frame builds new data from the last; there is nothing to diff.
      structuralSharing: false,
      meta: { streamedQuery: { owner, variables, stream } } satisfies StreamedMeta,
    }) as UseQueryOptions<Data, Error, Data>;

  const queryKeyOf = (variables: Variables) => options.query(variables).queryKey;

  const setStatus = (queryHash: string, status: StreamStatus) => {
    if (status === "idle") {
      statuses.delete(queryHash);
    } else {
      statuses.set(queryHash, status);
    }
    for (const listener of statusListeners) {
      listener();
    }
  };

  const applyBatch = (variables: Variables, queryKey: QueryKey, events: readonly Event[]) => {
    // A stream keeps data current; it never creates it. Data that was removed (a logout clears
    // the cache) stays removed, whatever the stream still delivers.
    if (!queryClient.getQueryCache().find({ queryKey, exact: true })) {
      return;
    }
    const previous = queryClient.getQueryData<Data>(queryKey);
    const data = options.reduce(previous, events);
    if (data != null && data !== previous) {
      queryClient.setQueryData<Data>(queryKey, data);
    }
    for (const listener of batchListeners) {
      listener({ variables, events, previous, data: data ?? previous });
    }
  };

  const open = (query: Query, variables: Variables) => {
    const { queryHash, queryKey } = query;
    const batch = createFrameBatch<Event>((events) => applyBatch(variables, queryKey, events));
    const stream: OpenStream = { closeTimer: null, gaveUp: false, close: () => {} };
    streams.set(queryHash, stream);
    setStatus(queryHash, "connecting");
    const connection = options.stream(variables, {
      sessionStorageKey: `sse:${queryHash}:last-event-id`,
      onOpen: () => setStatus(queryHash, "connected"),
      onClose: () => setStatus(queryHash, "reconnecting"),
      onReconnect: () => setStatus(queryHash, "reconnecting"),
      onReconnectExhausted: () => {
        stream.gaveUp = true;
        setStatus(queryHash, "error");
      },
      onEvent: batch.push,
    });
    stream.close = () => {
      streams.delete(queryHash);
      batch.close();
      connection.disconnect();
      setStatus(queryHash, "idle");
    };
  };

  const close = (queryHash: string) => {
    const stream = streams.get(queryHash);
    if (stream?.closeTimer) {
      clearTimeout(stream.closeTimer);
    }
    stream?.close();
  };

  const reconcile = (query: Query, variables: Variables, isNewReader: boolean) => {
    const stream = streams.get(query.queryHash);
    const isRead =
      query.state.data != null &&
      query.observers.some(
        (observer) =>
          observer.options.enabled !== false &&
          (observer.options.meta as StreamedMeta | undefined)?.streamedQuery?.stream !== false
      );
    if (isRead && stream?.gaveUp && isNewReader) {
      // Coming back to data whose stream gave up connects again.
      close(query.queryHash);
      open(query, variables);
    } else if (isRead && !stream) {
      open(query, variables);
    } else if (isRead && stream?.closeTimer) {
      clearTimeout(stream.closeTimer);
      stream.closeTimer = null;
    } else if (!isRead && stream && !stream.closeTimer) {
      stream.closeTimer = setTimeout(() => close(query.queryHash), CLOSE_DELAY_MS);
    }
  };

  queryClient.getQueryCache().subscribe((event) => {
    const streamed = (event.query.meta as StreamedMeta | undefined)?.streamedQuery;
    if (streamed?.owner !== owner) {
      return;
    }
    if (event.type === "removed") {
      close(event.query.queryHash);
    } else {
      // This definition's own meta, so its variables are this definition's.
      reconcile(event.query, streamed.variables as Variables, event.type === "observerAdded");
    }
  });

  const subscribeToStatuses = (listener: () => void) => {
    statusListeners.add(listener);
    return () => statusListeners.delete(listener);
  };

  return {
    queryOptions,
    /**
     * Reads the data, and whether loading it failed with nothing to show. A mounted reader keeps
     * the stream open unless it passes `stream: false`.
     */
    useQuery<Selected = Data>(
      variables: Variables,
      readOptions: {
        enabled?: boolean;
        select?: (data: Data) => Selected;
        /** A read that only needs what was loaded, such as a finished thread's summary. */
        stream?: boolean;
        /** Hands a load that fails with nothing to show to the nearest error boundary. */
        throwOnError?: boolean;
      } = {}
    ): { data: Selected | undefined; failed: boolean } {
      const { data, isError } = useQuery({
        ...queryOptions(variables, readOptions.stream),
        enabled: readOptions.enabled,
        select: readOptions.select as ((data: Data) => Data) | undefined,
        // Data that loaded stays on screen when a later refetch fails.
        throwOnError: readOptions.throwOnError
          ? (_error: Error, query: Query<Data, Error, Data>) => query.state.data == null
          : false,
      });
      // Failed means there is nothing to show; a failed refetch keeps what loaded.
      return { data: data as Selected | undefined, failed: isError && data == null };
    },
    useStreamStatus(variables: Variables): StreamStatus {
      const queryHash = hashKey(queryKeyOf(variables));
      return useSyncExternalStore(subscribeToStatuses, () => statuses.get(queryHash) ?? "idle");
    },
    getQueryData: (variables: Variables) => queryClient.getQueryData<Data>(queryKeyOf(variables)),
    setQueryData: (variables: Variables, data: Data) =>
      queryClient.setQueryData<Data>(queryKeyOf(variables), data),
    /** The cached data, or the loaded data once it arrives. */
    ensureQueryData: (variables: Variables) => queryClient.ensureQueryData(queryOptions(variables)),
    /** Fetches the server's copy, which replaces what the cache holds. */
    fetchQuery: (variables: Variables) =>
      queryClient.fetchQuery({ ...queryOptions(variables), staleTime: 0 }),
    removeQuery: (variables: Variables) =>
      queryClient.removeQueries({ queryKey: queryKeyOf(variables), exact: true }),
    /** Hears every frame of events after it is applied. */
    subscribe(listener: (batch: StreamedBatch<Variables, Data, Event>) => void) {
      batchListeners.add(listener);
      return () => {
        batchListeners.delete(listener);
      };
    },
  };
}

export type StreamedQueryDefinition<Variables, Data, Event> = ReturnType<
  typeof createStreamedQueryDefinition<Variables, Data, Event>
>;

/**
 * Collects events into one batch per animation frame. A hidden tab paints no frames, so there
 * events apply as they arrive; a turn that ends in a background tab must still land. Once
 * closed, an event that still arrives is applied on its own: its cursor has already advanced,
 * so dropping it would lose it.
 */
function createFrameBatch<Event>(apply: (events: Event[]) => void) {
  let events: Event[] = [];
  let frame: number | null = null;
  let isClosed = false;

  const drain = () => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
    if (events.length > 0) {
      const batch = events;
      events = [];
      apply(batch);
    }
  };

  return {
    push(event: Event) {
      if (isClosed) {
        apply([event]);
        return;
      }
      events.push(event);
      if (events.length >= MAX_BATCH_SIZE || document.visibilityState === "hidden") {
        drain();
      } else if (frame === null) {
        frame = requestAnimationFrame(() => {
          frame = null;
          drain();
        });
      }
    },
    close() {
      isClosed = true;
      drain();
    },
  };
}
